import { createContext, useContext, useReducer } from "react";

const FormContext = createContext(null);

const FormDispatchContext = createContext(null);

/**
 * FormProvider is a React component that provides a steps context for its child components.
 *
 * @param {object} options - The options for the FormProvider component.
 * @param {JSX.Element} options.children - The child components that will have access to the steps context.
 * @returns {JSX.Element} - The JSX element containing the steps context for the child components.
 * @constructor
 */
export function FormProvider({ children, fields }) {
  const initialForm = createInitialForm(fields);
  const [tasks, dispatch] = useReducer(formReducer, initialForm);

  return (
    <FormContext.Provider value={tasks}>
      <FormDispatchContext.Provider value={dispatch}>
        {children}
      </FormDispatchContext.Provider>
    </FormContext.Provider>
  );
}

/**
 * Returns the FormContext value
 */
export function useForm() {
  return useContext(FormContext);
}

/**
 * Returns the steps dispatch function from the context.
 */
export function useFormDispatch() {
  return useContext(FormDispatchContext);
}

const createInitialForm = (fields = []) => {
  const initialForm = {
    step: 1,
  };

  fields.forEach((field) => {
    switch (field.type) {
      case "singleLine": {
        initialForm[field.key] = "";
        break;
      }
      case "multiLine": {
        initialForm[field.key] = "";
        break;
      }
      case "image": {
        initialForm[field.key] = null;
        break;
      }
      case "projectUpload": {
        initialForm[field.key] = null;
        break;
      }
      case "tags": {
        initialForm[field.key] = [];
        break;
      }
      case "selection": {
        initialForm[field.key] = null;
        break;
      }
      default:
        return <div>No component found</div>;
    }
  });

  return initialForm;
};

function formReducer(formdata, action) {
  switch (action.type) {
    case "setStep": {
      const toStep = Math.min(action.step + 1, Object.keys(formdata).length);
      return {
        ...formdata,
        step: toStep,
      };
    }

    case "nextStep": {
      const nextStep = Math.min(
        formdata.step + 1,
        Object.keys(formdata).length,
      );

      return {
        ...formdata,
        step: nextStep,
      };
    }

    case "prevStep": {
      const prevStep = Math.max(formdata.step - 1, 1);

      return {
        ...formdata,
        step: prevStep,
      };
    }

    case "updateField": {
      const { type: _type, ...fieldUpdates } = action; // remove type

      return {
        ...formdata,
        ...fieldUpdates,
      };
    }

    default:
      return formdata;
  }
}
